import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { logInfo, logWarning } from '@/lib/logging-utils';
import type { UIMediaItem } from '@/types/ui-media';

const PLAYABLE_VIDEO_HOSTS = ['video.twimg.com', 'ton.twimg.com'];
const PLAYABLE_VIDEO_SUFFIXES = ['.mp4', '.m3u8'];

export const VIDEO_VARIANT_CONTENT_TYPE = 'video/mp4';

export function isPlayableVideoUrl(value: string | undefined | null): boolean {
  if (!value) {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  const lower = trimmed.toLowerCase();
  if (lower.startsWith('blob:') || lower.startsWith('data:video/')) {
    return true;
  }

  try {
    const parsed = new URL(trimmed);
    const hostname = parsed.hostname.toLowerCase();
    if (PLAYABLE_VIDEO_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))) {
      return true;
    }
    const pathname = parsed.pathname.toLowerCase();
    if (PLAYABLE_VIDEO_SUFFIXES.some((suffix) => pathname.endsWith(suffix))) {
      return true;
    }
  } catch {
    // Ignore URL parsing errors and fall through to suffix heuristics
  }

  if (PLAYABLE_VIDEO_SUFFIXES.some((suffix) => lower.includes(suffix))) {
    return true;
  }

  return false;
}

function sortVariantsByBitrate(variants: UIMediaItem['variants']): NonNullable<UIMediaItem['variants']> {
  const list = Array.isArray(variants) ? variants : [];
  return toSortedCompat(list, (a, b) => (b.bitRate || 0) - (a.bitRate || 0));
}

export function selectBestVideoVariant(media: UIMediaItem): string {
  if (media.type !== 'video' && media.type !== 'animated_gif') {
    return media.url ?? '';
  }

  if (isPlayableVideoUrl(media.url)) {
    return media.url ?? '';
  }

  const variants = Array.isArray(media.variants) ? media.variants : [];
  if (variants.length === 0) {
    logInfo(
      'TweetMedia - No playable video variants available',
      {
        mediaKey: media.mediaKey,
        url: media.url,
        variants,
      },
      'ui'
    );
    return '';
  }

  const playableVariants = sortVariantsByBitrate(
    variants.filter((variant) => variant.contentType === VIDEO_VARIANT_CONTENT_TYPE && isPlayableVideoUrl(variant.url))
  );

  if (playableVariants.length > 0 && playableVariants[0]?.url) {
    return playableVariants[0].url;
  }

  const playableFallbacks = variants.filter((variant) => isPlayableVideoUrl(variant.url));
  const mp4Fallback = playableFallbacks.find((variant) => (variant.url ?? '').toLowerCase().includes('.mp4'));
  if (mp4Fallback?.url) {
    return mp4Fallback.url;
  }

  if (playableFallbacks[0]?.url) {
    return playableFallbacks[0].url;
  }

  logWarning(
    'TweetMedia - No playable video variant URL resolved',
    {
      mediaKey: media.mediaKey,
      url: media.url,
      variants,
    },
    'ui'
  );
  return '';
}
